import type { AiMode } from "@/features/ai-agent/types/ai-mode";
import type { FollowUpHistoryCredit } from "@/features/follow-up/types";
import type {
  DeclarableOrigin,
  ContactOrigin,
} from "@/features/contact/constants/origin";
import type { AiPending } from "@/features/ai-agent/types/agent-step";
import type { Activity } from "@/types/activity";
import type { ChannelType } from "@/types/channel";
import type { ClosingNoteSubmission } from "@/types/closing-note";
import type { ContactDynamicValues } from "@/types/contact-field";
import type { ConversationStatus } from "@/types/conversation";
import type { ReportUser } from "@/features/reports/common/types";
import type { LeadSourceGroup } from "@/features/reports/leads/types";
import type { Message, Owner } from "@/types/message";
import type { AppointmentStatus } from "@/features/clinic/types/appointment";
import type { OrderStatus } from "@/types/order";

import { User } from "@/types/user";

export const CONTACT_EVENT_TYPE = {
  ACTIVITY: "activity",
  MESSAGE: "message",
} as const;

export type ContactEventType =
  (typeof CONTACT_EVENT_TYPE)[keyof typeof CONTACT_EVENT_TYPE];

/** How a contact came to stop getting broadcasts. */
export const OPT_OUT_SOURCE = {
  /** STOP or UNSUBSCRIBE in one of their first two messages after a broadcast. */
  REPLY: "reply",
  /** WhatsApp refused a broadcast: they turned off the business's promotions in the app. */
  WHATSAPP: "whatsapp",
  TEAMMATE: "teammate",
} as const;

export type OptOutSource = (typeof OPT_OUT_SOURCE)[keyof typeof OPT_OUT_SOURCE];

// Interfaces
export type Contact = {
  id: string;
  firstName: string | null;
  lastName: null | string;
  username: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  language: string | null;
  country: string | null;
  imageUrl: string;
  blocked: boolean;
  /**
   * Set while they get no broadcasts; normal chat is untouched. Optional
   * because older cached contacts and fixtures predate it.
   */
  optedOutAt?: string | null;
  optOutSource?: OptOutSource | null;
  lifecycleId: string | null;
  /**
   * The closing note their last conversation close or appointment outcome
   * named. Optional because older cached contacts and fixtures predate it.
   */
  lastClosingNoteId?: string | null;
  lastClosingNoteAt?: string | null;
  /**
   * First-touch acquisition origin, immutable once written. Null on contacts
   * created before the ATTR-04 backfill, which read as "Not recorded".
   */
  origin: ContactOrigin | null;
  referredByName: string | null;
  sortOrder: number | null;
  assignedUserId: string | null;
  assignedUser: User | null;
  assignedTeamId: string | null;
  /** What Echo does here. Who is assigned is a separate question. */
  aiMode: AiMode;
  /** A timed mode (the inbox's pause): at this time the mode goes back to
   *  `aiModeRevertTo`. Null when the mode stands. */
  aiModeUntil: string | null;
  aiModeRevertTo: AiMode | null;
  lastMessageProcessedAt: string;
  lastMessageOwner: string;
  lastMessageId: string;
  lastBumpedAt: string;
  createdAt: string;
  updatedAt: string;
  tags: Array<string>;
  /**
   * Values for the workspace's own contact fields, keyed by field key. A key
   * absent from here has no value on this contact - which is the normal state
   * for a ClinicSoft MRN, since a third of patients have none when created.
   */
  dynamicValues?: ContactDynamicValues | null;
  treatmentIds: string[];
  productIds: string[];
  adIds: string[];
  websiteIds: string[];
  channels: Array<ContactChannel>;
  interactions: ContactInteraction[];
  status: ConversationStatus;
  snoozedUntil: string | null;
  followUp: ContactFollowUp | null;
  unreadCount: number;
  unreadMentionCount: number;
  lastSummary: string | null;
  lastNote: string | null;
  lastNoteAt: string | null;
  lastAd: LastAdSnapshot | null;
  lastWebsiteVisit: LastWebsiteVisitSnapshot | null;
  lastFormSubmissionId?: string | null;
  lastFormSubmission?: LastFormSubmissionSnapshot | null;
  /** Null when the viewer's role can't see amounts (`reports.amount`). */
  value: string | null;
  activeWorkflowCount?: number;
  humanAgentRequired: boolean;
  /** The AI logged an unanswered knowledge gap; cleared once the answer is
   *  captured, accepted, or dismissed. */
  knowledgeGapPending: boolean;
  /** What Echo is waiting on a person for (a draft, questions, approvals), or
   *  null when nothing. */
  aiPending: AiPending | null;
  /** Clinic list rows only. */
  lastVisit?: ContactLastVisit | null;
  nextVisitAt?: string | null;
  /** Null when the viewer's role can't see amounts, like `value`. */
  paidToDate?: string | null;
  /** Store list rows only. */
  ordersCount?: number;
  lastOrder?: ContactLastOrder | null;
  spent?: string | null;
};

export type ContactLastVisit = { at: string; status: AppointmentStatus };

export type ContactLastOrder = { at: string; status: OrderStatus };

/**
 * What `PATCH contacts/:id/interact` reports back: the viewing user's unread
 * counters as they stand after the view cutoff is written. Authoritative on
 * purpose - a message or mention landing mid-request means "the view cleared
 * everything" isn't safe to assume client-side.
 */
export type ContactInteractionResult = {
  unreadCount: number;
  unreadMentionCount: number;
};

export type FollowUpSource = "user" | "workflow" | "system" | "ai";

/** How a follow-up stopped being the contact's active one. */
export const FOLLOW_UP_OUTCOME = {
  COMPLETED: "completed",
  CLEARED: "cleared",
  SUPERSEDED: "superseded",
} as const;

export type FollowUpOutcome =
  (typeof FOLLOW_UP_OUTCOME)[keyof typeof FOLLOW_UP_OUTCOME];

export type ContactFollowUp = {
  id: string;
  reason: string | null;
  dueAt: string;
  source: FollowUpSource;
  /** The person who set it, when `source` is `user`. */
  createdByUserId?: string | null;
  /** Null while this is the active follow-up; set when it was closed. */
  closedAt?: string | null;
  outcome?: FollowUpOutcome | null;
  /** Who closed it; null while open or on rows closed before this was kept. */
  closedBySource?: FollowUpSource | null;
  closedByUserId?: string | null;
  /** Whether the teammate said it led to the booking or order; null when not asked. */
  /** When it was set. */
  createdAt?: string;
  reminderSentAt: string | null;
  /** What it led to, live credits only; absent on the active row and older history. */
  credits?: FollowUpHistoryCredit[];
};

export type LastAdSnapshot = {
  id: string;
  name: string;
  /** The contact payload carries the ad itself, which has no click time. */
  clickedAt?: string | null;
};

export type LastFormSubmissionSnapshot = {
  id: string;
  formId: string;
  submittedAt: string;
  form?: {
    id: string;
    name: string;
    source: string;
  } | null;
};

export type LastWebsiteVisitSnapshot = {
  websiteId?: string | null;
  websiteName?: string | null;
  url?: string | null;
  domain?: string | null;
  pagePath?: string | null;
  pageTitle?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmTerm?: string | null;
  visitedAt?: string | null;
};

export type ContactOriginActor = "user" | "api" | "ai" | "system";

/** One thing the contact did, or one thing done about them, that the lead record keeps. */
export type ContactTouchType =
  | "inbound_message"
  | "ad_click"
  | "form_fill"
  | "broadcast_reply"
  | "referral"
  | "manual"
  | "api"
  | "crm_sync"
  | "import"
  | "stage_reset"
  | "broadcast_sent"
  | "website_visit"
  | "sync"
  | "stage_closed";

export type ContactOutcomeType =
  | "booked"
  | "attended"
  | "no_show"
  | "cancelled"
  | "paid"
  | "ordered"
  | "delivered";

export type ContactTouchDetail = {
  sourceUrl?: string | null;
  ref?: string | null;
  origin?: string | null;
  clickId?: string | null;
  externalSource?: string | null;
  fromLifecycleId?: string | null;
  toLifecycleId?: string | null;
  stageKind?: string | null;
  templateId?: string | null;
};

export type ContactEpisodeTouch = {
  type: ContactTouchType;
  occurredAt: string;
  platform: string | null;
  detail: ContactTouchDetail | null;
  /** Who made a stage change, as the activity it came from records it. */
  owner: ContactOriginActor | null;
  userName: string | null;
};

export type ContactEpisodeOutcome = {
  type: ContactOutcomeType;
  entityType: string;
  entityId: string;
  occurredAt: string;
  amount: number | null;
};

/** One time the contact was a lead: opened by a touch, closed by a win, a loss or silence. */
export type ContactEpisode = {
  sequence: number;
  startedAt: string;
  /** When they counted as a lead: the opener, or the first customer touch that took over a dormant record. */
  leadAt: string;
  isNew: boolean;
  group: LeadSourceGroup;
  platform: string | null;
  touches: ContactEpisodeTouch[];
  outcomes: ContactEpisodeOutcome[];
  endedAt: string | null;
  endedKind: "won" | "lost" | null;
};

/** How a contact came to exist, from `GET contacts/:id/origin`. */
export type ContactOriginDetails = {
  origin: ContactOrigin | null;
  createdAt: string;
  /** What the origin points at, as the reports show it: the referrer, ad
   *  campaign, first channel, API form or source, file or CRM. */
  sourceDetail: string | null;
  createdBy: ContactOriginActor | null;
  /** The teammate who added or imported the contact. */
  createdByUser: ReportUser | null;
  /** e.g. "Created from 'Clinic WhatsApp'". */
  createdDescription: string | null;
  import: {
    id: string;
    name: string;
    totalCount: number;
    createdAt: string;
  } | null;
  firstChannel: {
    type: ChannelType;
    name: string;
    firstMessageAt: string;
  } | null;
  /** Newest first. */
  episodes: ContactEpisode[];
};

/** One step of the drawer's Origin timeline. */
export type ContactOriginLine = {
  id: "created" | "first-message" | "form";
  /** ISO time, for ordering the timeline. */
  at: string;
  title: string;
  detail: string | null;
};

export type ContactOriginContext = Pick<
  Contact,
  "referredByName" | "lastAd" | "lastFormSubmission"
>;

export type ContactOriginHintContext = Pick<Contact, "origin" | "createdAt">;

export type ContactChannel = {
  id: string;
  type: ChannelType;
  identifier: string;
  lastMessageSentByUserAt: string | null;
  lastCommentSentByUserAt: string | null;
  lastMessageSentAt: string;
  lastMessageSentBy: Owner;
  metadata?: {
    isSyncing?: boolean;
    [key: string]: unknown;
  };
};

export type BulkTransferContactsPayload = {
  contactIds: string[];
  assignedUserId?: string;
  assignedTeamId?: string;
  description?: string;
};

export type ContactAssignmentResolution = {
  contactId: string;
  assignedUserId: string | null;
  assignedTeamId: string | null;
};

export type BulkTransferContactsResult = {
  transferredContactIds: string[];
  teamUpdatedContactIds: string[];
  assignments: ContactAssignmentResolution[];
};

export type PreviewTeamTransferPayload = {
  contactIds: string[];
  assignedTeamId: string;
};

export type PreviewTeamTransferResult = {
  assignments: ContactAssignmentResolution[];
};

export type BulkCloseContactsPayload = {
  contactIds: string[];
  /** One note, recorded on every contact in the selection. */
  closingNote?: ClosingNoteSubmission;
  completeFollowUps?: boolean;
};

export type BulkTagContactsPayload = {
  contactIds: string[];
  addTagIds?: string[];
  removeTagIds?: string[];
};

export type BulkNoteContactsPayload = {
  contactIds: string[];
  text: string;
};

export type BulkContactsActionResult = {
  affectedContactIds: string[];
};

export type UpdateContactPayload = Pick<Contact, "id"> &
  Partial<Omit<Contact, "id" | "value" | "followUp">> & {
    closingNote?: ClosingNoteSubmission;
    followUp?: {
      reason?: string | null;
      dueAt?: string;
    } | null;
    followUpCompleted?: boolean;
    /** Stops or restarts broadcasts; the server stamps `optedOutAt` and the source. */
    optedOut?: boolean;
    /** Number, not the string `Contact.value` is read back as: the API validates
     *  this as a number and rejects the formatted string. */
    value?: number;
  };

export type CreateContactPayload = Partial<
  Pick<
    Contact,
    | "firstName"
    | "lastName"
    | "email"
    | "phone"
    | "address"
    | "lifecycleId"
    | "tags"
  >
> & {
  dynamicValues?: ContactDynamicValues;
  /**
   * Declared outright rather than derived. Constrained to the curated
   * declarable subset: the DTO accepts the whole enum, so this type is what
   * keeps a machine-set origin off the create payload.
   */
  origin?: DeclarableOrigin;
  /**
   * Staff-declared referrer, creation-only. Deliberately absent from
   * `UpdateContactPayload`: the backend omits both from its update DTO, and the
   * stored `origin` is derived from their presence here - sending them on an
   * edit would rewrite a first touch.
   */
  referredByName?: string | null;
};

export type SoundNotification = {
  url: string;
  notifyUserIds: string[];
};

export type ContactEvent =
  | {
      id: string;
      type: "activity";
      activity: Activity;
      createdAt: string;
      contactId: string;
      sound?: SoundNotification;
    }
  | {
      id: string;
      type: "message";
      message: Message;
      createdAt: string;
      contactId: string;
      sound?: SoundNotification;
    };

export type ContactInteraction = {
  userId: string;
  interactedAt: Date;
  lastViewedAt?: Date | string | null;
  contactId: string;
  unreadCount: number;
};

// Backwards compatibility aliases
export type UpdateContact = UpdateContactPayload;
export type CreateContact = CreateContactPayload;
